import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import { useMemo, useRef, useState } from 'react';
import { isHtmlEmpty } from 'utils';
import { parseDateSafe } from 'utils/timeHelper';
import {
  useGetRunbookViewed,
  useRunbookLog,
} from 'infrastructure/hooks/clinic';
import colors from 'assets/styles/colors';
import { useTranslation } from 'react-i18next';

import s from '../styles.module.scss';
import RunbookItems from '../components/items';

interface Props {
  clinicGuid: string;
  patientGuid: string;
}
export enum Module {
  INSTRUCTION = 'INSTRUCTION',
  ACTIVITY_LOG = 'ACTIVITY_LOG',
}

export const useRunbook = ({ clinicGuid, patientGuid }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [initialModule, setInitialModule] = useState<Module>(
    Module.INSTRUCTION,
  );
  const scrollableElementRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { runbookViewed, runbookViewedLoading } = useGetRunbookViewed({
    clinicGuid,
    patientGuid,
  });

  const { runbookLogs, loading } = useRunbookLog({
    meta: {
      clinicGuid,
      items: 100,
    },
  });

  const [instructionData, ...history] = runbookLogs?.items ?? [];
  const activityLogData = history?.map((el, index) => ({
    key: String(index + 1),
    label: (
      <div className={s['collapse-label']}>
        <span className={s['collapse-date']}>{el.createdAt}</span>
        <span className={s['collapse-user']}>{el?.updateByFullName}</span>
      </div>
    ),

    children: <RunbookItems data={el} />,
    style: {
      background: index % 2 === 0 ? colors.cardBody : colors.cascadingWhite,
    },
  }));

  const isDataEmpty = useMemo(
    () => isHtmlEmpty(instructionData),
    [instructionData],
  );

  const formattedUpdatedAt = useMemo(() => {
    const date = parseDateSafe(instructionData?.createdAt);
    if (!date) return null;
    return DateFormatter({ date, format: DateFormats['MM-dd-yyyy | h:mm a'] });
  }, [instructionData?.createdAt]);

  const handleTabClick = (tabKey: Module) => {
    setInitialModule(tabKey);

    if (scrollableElementRef && scrollableElementRef.current) {
      scrollableElementRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };
  const tabs: { key: Module; label: string }[] = [
    { key: Module.INSTRUCTION, label: t('runbook.instruction') },
    { key: Module.ACTIVITY_LOG, label: t('runbook.activityLog') },
  ];

  return {
    currentModule: initialModule,
    expanded,
    isDataEmpty,
    loading: loading || runbookViewedLoading,
    formattedUpdatedAt,
    tabs,
    scrollableElementRef,
    instructionData,
    history,
    activityLogData,
    hasUpdate: !runbookViewed?.exists,
    handleTabClick,
    setExpanded,
  };
};
